import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import { RiArrowLeftSLine, RiCheckLine, RiBankCardLine, RiErrorWarningLine, RiExternalLinkLine } from "react-icons/ri";
import liff from "@line/liff";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useAuthReady } from "@provider/LineLoginProvider";
import { getRuntimeLiffId } from "@utils/line/getLiffId";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { setPayReturnTo } from "@utils/payReturnTo";
import { formatGasTitle } from "@utils/func/gasTitle";
import { appToast } from "@components/Toast";
import { AddCardRow, CardBrand, PaymentOptionRow } from "@components/payment/PaymentOptionRow";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";
import { isPayLinkMode } from "@utils/payLink";
import { shouldOfferApplePayInLine } from "@utils/applePay";
import ApplePayButton from "@components/payment/ApplePayButton";

import SectionLabel from "../Account/components/ui/SectionLabel";
import { getOrderDetails } from "../OrderList/API/handleOrderAPI";
import {
  handleAPIGetOrderPaymentStatus,
  handleAPIOpenOrderPaymentPage,
  handleAPIListContextMethods,
  handleAPICreatePaymentIntent,
  handleAPIIssuePayToken,
  handleAPIGetPaymentViewer,
  newPaymentAttemptKey,
  type PaymentViewer,
  type OrderPaymentStatus,
  type ContextPaymentMethod,
  type SnapshotItem,
} from "../Account/API/handlePaymentAPI";
import PayLinkView from "./PayLinkView";
import { AddFriendCard, useOaFriendship } from "./AddFriend";
import { OaRedirectCountdown, useJustPaid } from "./OaRedirectCountdown";
import { notPayableHint } from "./notPayableHint";
import { PaymentProcessingView, usePaymentResultPoll } from "./PaymentProcessing";
import { PaymentLockCountdownNote, usePaymentLockCountdown } from "./PaymentLockCountdown";

const ipassMoneyLogo = require("@styles/images/payment/ipass_money.png") as string;

type View_ = "loading" | "error" | "paid" | "not_payable" | "payable" | "processing" | "success" | "external_pending";

/** 訂單狀態中文標籤（與訂單列表 STS 一致） */
const ORDER_STATUS_LABEL: Record<string, string> = {
  pending: "待確認",
  waiting_for_pickup: "待接單",
  delivering: "配送中",
  completed: "已完成",
  canceled: "已取消",
};
const orderStatusLabel = (s?: string | null) => (s && ORDER_STATUS_LABEL[s]) || s || "-";

/**
 * 訂單付款頁 /pay/:orderId（orderId = platform_order_id UUID）。
 * 兩種進站模式：
 *   - LINE 模式：既有 LIFF 登入（LINE 推播的付款連結），走這個檔案內的 PayOrderLineMode。
 *   - 付款連結模式（?pt=token）：配送員出示的 QR、負責人轉傳的連結（訪客付款，2026-10-19）。
 *     付款權限由 pt 決定；有登入就先問後端「你是誰」——付款人／可付款成員照樣走 LINE 模式
 *     （可用已綁的卡），其餘（未註冊、不是家庭成員、沒有付款權限的成員）走 PayLinkView 輸入卡號。
 */
export const PayOrder = () => {
  // 凍結掛載當下的模式判斷：PayLinkView 讀完 pt 會 replaceState 把它從網址列移除，
  // 若每次 render 重新呼叫 isPayLinkMode()，那之後任何一次重新渲染都會判斷成「不是 pay-link 模式」，
  // 導致分派器把已經在跑的 PayLinkView 整個換成 PayOrderLineMode（後者又需要 cusJwtToken，直接卡死）。
  const [payLinkMode] = useState(() => isPayLinkMode());
  if (payLinkMode) {
    return <PayLinkGate />;
  }
  return <PayOrderLineMode />;
};

/** 付款連結的身分分流：等登入塵埃落定，有 JWT 才問後端；沒有（未註冊、LINE 外開）就是訪客。 */
const PayLinkGate = () => {
  const { orderId } = useParams();
  const authReady = useAuthReady();
  const { cusJwtToken } = useCusAuth();
  const [viewer, setViewer] = useState<PaymentViewer | null>(null);

  useEffect(() => {
    if (!authReady || viewer) return undefined;
    if (!cusJwtToken || !orderId) {
      setViewer("guest");
      return undefined;
    }
    let alive = true;
    handleAPIGetPaymentViewer(orderId, cusJwtToken).then((v) => {
      if (!alive) return;
      // 走 LINE 模式就用不到 pt：從網址列拿掉，避免留在瀏覽紀錄
      if (v === "payer" || v === "payer_member") window.history.replaceState(null, "", window.location.pathname);
      setViewer(v);
    });
    return () => {
      alive = false;
    };
  }, [authReady, cusJwtToken, orderId, viewer]);

  if (!viewer) {
    return (
      <YStack height="100dvh" width="100vw" ai="center" jc="center" background={colors.bg}>
        <Spinner size="large" color={colors.primary} />
      </YStack>
    );
  }
  if (viewer === "payer" || viewer === "payer_member") return <PayOrderLineMode />;
  return <PayLinkView viewer={viewer} />;
};

/**
 * LINE 模式付款頁本體。入口：送達後 LINE 推播的付款連結。走既有 LINE 登入；後端驗 caller 為訂單 payer，
 * 連結轉傳給他人無法付款。扣款打 POST /payments/intents（同步、防重複扣款、金額後端反查）。
 * Apple Pay（訂單方式為 apple_pay 且在 iPhone LINE 內）另開 Safari 完成，見 openApplePayExternal。
 * 一卡通 MONEY（訂單方式為 ipass_twqr，配送員現場改的）同樣外開付款連結頁完成，見 openIpassTwqrExternal。
 */
const PayOrderLineMode = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();

  const [view, setView] = useState<View_>("loading");
  const [errMsg, setErrMsg] = useState("");
  const [status, setStatus] = useState<OrderPaymentStatus | null>(null);
  const [items, setItems] = useState<SnapshotItem[]>([]);
  const [displayId, setDisplayId] = useState<string>("");
  const [supplierName, setSupplierName] = useState<string>("");
  const [cards, setCards] = useState<ContextPaymentMethod[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [paying, setPaying] = useState(false);
  // 這次付款嘗試的冪等鍵：網路斷掉重按要用同一把（後端回原交易，不會再扣一次）；
  // 有了確定結果（成功或被拒）才換新的
  const attemptKey = useRef<string | null>(null);
  const [issuingToken, setIssuingToken] = useState(false);
  const [sharing, setSharing] = useState(false);
  // 付款成功後偵測是不是瓦斯行官方帳號好友，不是就顯示「加入好友」（與付款連結頁一致）
  const isOaFriend = useOaFriendship();
  // 剛付完才倒數 5 秒回到瓦斯行官方帳號（2026-10-28）；之後打開已付款的單不跳
  const justPaid = useJustPaid(view);
  const [refreshing, setRefreshing] = useState(false);
  // 已簽發的一次性付款頁網址：external_pending 畫面「重新開啟付款頁」用，不重新發 token。
  const [externalPayUrl, setExternalPayUrl] = useState<string>("");
  const [successInfo, setSuccessInfo] = useState<{ amount: number; label: string; time: string } | null>(null);
  // 開頁（POST …/open，開始請款鎖）只在第一次載入打一次：StrictMode 重跑 effect 時共用同一個請求，
  // 不會 open 一次又在 open 回來前搶先打 GET；之後換 token 等重跑一律用 GET
  const openRequest = useRef<{ key: string; promise: ReturnType<typeof handleAPIGetOrderPaymentStatus> } | null>(null);
  const recheckingLock = useRef(false);

  const cardLabel = (c: ContextPaymentMethod) => `信用卡 •••• ${c.card?.lastfour ?? "----"}`;

  // 掃碼付款連結帶 invite_code：未註冊者會被導去註冊+加入家庭流程，記下這個付款頁，
  // 讓 JoinSuccess 完成後可以導回來繼續付款。要放在最早的 effect，
  // 確保比未註冊被導去註冊的時機更早寫入。
  useEffect(() => {
    if (orderId && new URLSearchParams(window.location.search).has("invite_code")) {
      setPayReturnTo(`/pay/${orderId}`);
    }
  }, [orderId]);

  useEffect(() => {
    if (!cusJwtToken || !orderId) return;
    let alive = true;
    // 第一次載入打 open（開始請款鎖）；open 失敗（例如舊後端沒有這支）退回 GET，頁面照常
    const loadStatus = () => {
      const key = `${orderId}:${cusJwtToken}`;
      if (openRequest.current) {
        return openRequest.current.key === key ? openRequest.current.promise : handleAPIGetOrderPaymentStatus(orderId, cusJwtToken);
      }
      const promise = handleAPIOpenOrderPaymentPage(orderId, cusJwtToken).then((opened) =>
        opened.state === "success" ? opened : handleAPIGetOrderPaymentStatus(orderId, cusJwtToken),
      );
      openRequest.current = { key, promise };
      return promise;
    };
    (async () => {
      const [st, detail] = await Promise.all([
        loadStatus(),
        getOrderDetails({ orderId, jwtToken: cusJwtToken, xContext: buildXContextHeader(chooseIdentity) }),
      ]);
      if (!alive) return;

      if (st.state !== "success") {
        setErrMsg(typeof st.error === "string" ? st.error : "無法載入付款資訊");
        setView("error");
        return;
      }
      setStatus(st.data);

      if (detail.state === "success") {
        const snap = detail.data?.data?.supplier_order_snapshot_json;
        setItems((snap?.items ?? []) as SnapshotItem[]);
        setDisplayId(snap?.order_list?.order_id ?? "");
        setSupplierName(detail.data?.data?.supplier?.supplier_name ?? chooseIdentity?.primary_supplier?.supplier_name ?? "");
      }

      // 撈可選卡：優先用「訂單付款人 context」（後端回傳），FM 深連結開頁時
      // 前端沒有 chooseIdentity.context_id，改用它才撈得到卡；否則退回當下身分。
      const contextId = st.data.context_id ?? chooseIdentity?.context_id ?? null;
      const ctxMethods = contextId ? await handleAPIListContextMethods(contextId, cusJwtToken) : null;
      if (!alive) return;
      const ctxItems = ctxMethods && ctxMethods.state === "success" ? ctxMethods.items : [];
      const list = ctxItems.filter((c) => c.method_type === "credit_card" && c.status === "active");
      setCards(list);

      // 預選順位：訂單快照選的卡 → 帳戶預設（含帳戶共用卡）→ 第一張
      const snapCard = st.data.payment_method_id && list.find((c) => c.payment_method_id === st.data.payment_method_id);
      const defId = ctxMethods && ctxMethods.state === "success" ? ctxMethods.defaultId : null;
      const defCard = defId && list.find((c) => c.payment_method_id === defId);
      setSelectedId((snapCard || defCard || list[0])?.payment_method_id ?? "");

      if (st.data.paid) {
        setView("paid");
        return;
      }
      if (st.data.payment_in_progress) {
        setView("processing");
        return;
      }
      if (!st.data.payable) {
        setView("not_payable");
        return;
      }
      setView("payable");
    })();
    return () => {
      alive = false;
    };
  }, [cusJwtToken, orderId]);

  const pay = async () => {
    if (!orderId || !cusJwtToken || !selectedId || paying) return;
    setPaying(true);
    attemptKey.current ??= newPaymentAttemptKey();
    const r = await handleAPICreatePaymentIntent(orderId, selectedId, cusJwtToken, attemptKey.current);
    setPaying(false);
    // 202：銀行還沒回，結果確認中——不是成功，也不能讓客戶再付一次
    if (r.state === "success" && r.data?.status !== "captured") {
      setView("processing");
      return;
    }
    if (r.state === "error" && r.httpStatus === undefined) {
      // 沒收到回應：可能已經扣款。保留同一把冪等鍵，再按一次後端會回原本那筆
      appToast.error("網路不穩，請再按一次付款，不會重複扣款");
      return;
    }
    attemptKey.current = null;
    if (r.state === "success") {
      const sel = cards.find((c) => c.payment_method_id === selectedId);
      setSuccessInfo({
        amount: status?.amount ?? r.data?.amount ?? 0,
        label: sel ? cardLabel(sel) : "信用卡",
        time: new Date().toLocaleString("zh-TW", { hour12: false }),
      });
      setView("success");
    } else {
      // 後端錯誤多為英文技術訊息，不可直接顯示給客戶；統一對應中文友善提示。
      const raw = typeof r.error === "string" ? r.error : "";
      let msg = "付款失敗，請稍後再試";
      if (/not authorized|not usable|another context|owner left/i.test(raw)) {
        msg = "此付款方式目前無法使用，請改用其他卡或聯絡瓦斯行";
      } else if (/already.*payment_transaction|active payment/i.test(raw)) {
        msg = "付款處理中，請稍候再試";
      } else if (/PAYMENT_REQUEST_EXPIRED/.test(raw)) {
        msg = "付款連結已過期（開啟超過 5 分鐘），請聯絡配送員重新請款";
        // 後端已取消請款：重新查一次，切到「連結已失效」畫面
        void recheckStatus();
      } else if (/PAYMENT_NOT_REQUESTED/.test(raw)) {
        msg = "配送員還沒請款，收到付款通知後再付款";
      } else if (/CASH_FALLBACK/.test(raw)) {
        msg = "配送員已改為現金收款，請以現金支付";
      } else if (/ALREADY_PAID/.test(raw)) {
        msg = "這筆訂單已完成付款";
      } else if (/not payable|not the payer/i.test(raw)) {
        msg = "此訂單目前無法線上付款";
      }
      appToast.error(msg);
    }
  };

  // 開啟一次性付款頁：LINE 內用 liff.openWindow 外開 Safari；桌面／dev（非 LINE 內）
  // 用 window.open 開新分頁。兩者都可能因瀏覽器封鎖彈窗等原因丟出例外，
  // 失敗僅 toast 提示，畫面仍切到 external_pending（重新開啟付款頁按鈕可再試一次）。
  const openPaymentUrl = (url: string) => {
    try {
      if (liff.isInClient()) {
        liff.openWindow({ url, external: true });
      } else {
        window.open(url, "_blank", "noopener");
      }
    } catch (e) {
      appToast.error("無法開啟付款頁，請點下方按鈕重試");
    }
  };

  // Apple Pay（LINE 內）：不在 LINE 內載 TapPay，按鈕只是入口——簽發一次性 pay_token
  // 後外開付款頁完成付款，回來這頁再手動刷新確認結果。
  const openApplePayExternal = async () => {
    if (!orderId || !cusJwtToken || issuingToken) return;
    setIssuingToken(true);
    const r = await handleAPIIssuePayToken(orderId, cusJwtToken);
    setIssuingToken(false);
    if (r.state === "success") {
      setExternalPayUrl(r.data.url);
      openPaymentUrl(r.data.url);
      setView("external_pending");
    } else {
      appToast.error("無法開啟 Apple Pay 付款，請稍後再試");
    }
  };

  // 一卡通 MONEY（TWQR 單筆，2026-11-03）：一卡通的 A1 → 導去 iPASS MONEY → 導回 → A2 整條流程做在
  // 付款連結頁（PayLinkView，配送員 QR 走的那頁），LINE 模式不重做一份——跟 Apple Pay 一樣簽發一次性
  // pay_token 外開那頁完成，回來這頁按「我已完成付款」確認。
  // 網址帶 auto=ipass：那頁一載入就自動打 A1 直接跳 iPASS MONEY，客戶不用再按第二次（Safari 那頁只是過場）。
  // 用本站 origin 組網址而不是後端給的 url：本機（localhost）測試時才會開到本機的付款連結頁；
  // 部署環境 LIFF endpoint 與 PAY_LINK_BASE_URL 本來就是同一個站。
  const openIpassTwqrExternal = async () => {
    if (!orderId || !cusJwtToken || issuingToken) return;
    setIssuingToken(true);
    const r = await handleAPIIssuePayToken(orderId, cusJwtToken);
    setIssuingToken(false);
    if (r.state === "success") {
      const url = `${window.location.origin}/pay/${orderId}?pt=${encodeURIComponent(r.data.pay_token)}&auto=ipass`;
      setExternalPayUrl(url);
      openPaymentUrl(url);
      setView("external_pending");
    } else {
      appToast.error("無法開啟一卡通付款，請稍後再試");
    }
  };

  // 請別人付款（訪客付款）：發一次性付款連結，用瓦斯行 LIFF 網址分享——對方不用註冊、
  // 不用加入家庭就能付（信用卡單次刷卡或 Apple Pay）。連結本身 30 分鐘內有效，但請款鎖從這頁打開就開始算
  // （5 分鐘、對方打開也不延長），所以有鎖時文案寫到期時間；配送員的 QR 不受影響。
  const shareToOthers = async () => {
    if (!orderId || !cusJwtToken || sharing) return;
    setSharing(true);
    const r = await handleAPIIssuePayToken(orderId, cusJwtToken);
    setSharing(false);
    if (r.state !== "success") {
      appToast.error("無法產生付款連結，請稍後再試");
      return;
    }
    const liffId = getRuntimeLiffId();
    const url = liffId ? `https://liff.line.me/${liffId}/pay/${orderId}?pt=${encodeURIComponent(r.data.pay_token)}` : r.data.url;
    const lockExpiresAt = status?.payment_lock_expires_at ? new Date(status.payment_lock_expires_at) : null;
    const validity =
      lockExpiresAt && Number.isFinite(lockExpiresAt.getTime())
        ? `請在 ${lockExpiresAt.toLocaleTimeString("zh-TW", { hour: "2-digit", minute: "2-digit", hour12: false })} 前付款`
        : "30 分鐘內有效";
    const text = `${supplierName || "瓦斯"}訂單的付款連結，點開就能付款，不用註冊（${validity}）：\n${url}`;
    try {
      if (liff.isApiAvailable("shareTargetPicker")) {
        await liff.shareTargetPicker([{ type: "text", text }]);
        return;
      }
    } catch {
      // LIFF 沒初始化或沒開分享功能：往下改用系統分享／複製
    }
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
    } catch {
      return; // 使用者取消分享
    }
    try {
      await navigator.clipboard.writeText(url);
      appToast.success("已複製付款連結，貼給要付款的人");
    } catch {
      appToast.error("無法分享，請稍後再試");
    }
  };

  // 「重新開啟付款頁」：沿用已簽發的 pay_token 網址重開，不重新打 issue-pay-token API。
  const reopenPaymentUrl = () => {
    if (!externalPayUrl) return;
    openPaymentUrl(externalPayUrl);
  };

  const refreshExternalPaymentStatus = async () => {
    if (!orderId || !cusJwtToken || refreshing) return;
    setRefreshing(true);
    const r = await handleAPIGetOrderPaymentStatus(orderId, cusJwtToken);
    setRefreshing(false);
    if (r.state !== "success") {
      appToast.error("無法取得付款狀態，請稍後再試");
      return;
    }
    setStatus(r.data);
    if (r.data.paid) {
      setView("paid");
      return;
    }
    appToast.error("尚未收到付款");
  };

  // 綁卡：走既有 TapPay 表單，綁完 returnTo 回本頁重新載入
  // returnContext：3DS challenge 完成後（§6），落地頁依此導回本頁繼續付款
  const goBindCard = () => {
    navigate("/account/paymentMethod", {
      state: {
        bindScope: "personal",
        returnTo: `/pay/${orderId}`,
        returnContext: { type: "pay_order", order_id: orderId ?? "", return_path: `/pay/${orderId}` },
      },
    });
  };

  const back = () => navigate(`/orders/${orderId}`);

  // 重新查付款狀態（GET，不延長請款鎖）並切畫面：請款鎖到期、或付款時後端回請款已過期時用
  async function recheckStatus() {
    if (!orderId || !cusJwtToken || recheckingLock.current) return;
    recheckingLock.current = true;
    try {
      const r = await handleAPIGetOrderPaymentStatus(orderId, cusJwtToken);
      if (r.state !== "success") return;
      setStatus(r.data);
      if (r.data.paid) setView("paid");
      else if (r.data.payment_in_progress) setView("processing");
      else if (!r.data.payable) setView("not_payable");
    } finally {
      recheckingLock.current = false;
    }
  }

  // 請款鎖倒數：時間到重新查一次，後端已取消請款就會切到「連結已失效」
  const lockRemaining = usePaymentLockCountdown(
    status?.payment_lock_expires_at,
    view === "payable",
    () => {
      void recheckStatus();
    },
    paying,
  );

  usePaymentResultPoll(
    view === "processing",
    async () => {
      if (!orderId || !cusJwtToken) return null;
      const r = await handleAPIGetOrderPaymentStatus(orderId, cusJwtToken);
      return r.state === "success" ? r.data : null;
    },
    (st) => {
      setStatus(st);
      attemptKey.current = null;
      if (st.paid) {
        setView("paid");
        return;
      }
      appToast.error("付款沒有成功，請再試一次或改用其他卡");
      setView(st.payable ? "payable" : "not_payable");
    },
  );

  const headerNode = (
    <Header>
      <XStack ai="center" onPress={back} cursor="pointer" mb="$3">
        <View mr="$2">
          <RiArrowLeftSLine size={20} color={colors.light} />
        </View>
        <Text color={colors.light} fontWeight="bold" fontSize={16}>
          返回
        </Text>
      </XStack>
      <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
        訂單付款
      </Text>
      <Text color="rgba(255,255,255,0.85)" fontSize={12}>
        {supplierName}
        {displayId ? ` · 訂單 ${displayId}` : ""}
      </Text>
    </Header>
  );

  if (view === "loading") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center">
          <Spinner size="large" color={colors.primary} />
        </YStack>
      </YStack>
    );
  }

  if (view === "error") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={14} color={colors.sub} ta="center">
            {errMsg}
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            請確認以正確的帳號開啟此付款連結
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "processing") {
    return <PaymentProcessingView header={headerNode} />;
  }

  if (view === "external_pending") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiExternalLinkLine size={40} color={colors.primary} />
          <Text fontSize={15} fontWeight="700" color={colors.text} ta="center">
            已在 Safari 開啟付款頁
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            完成付款後回到這裡
          </Text>
          <YStack width="100%" gap="$2" mt="$4">
            <YStack
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={reopenPaymentUrl}
            >
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                重新開啟付款頁
              </Text>
            </YStack>
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              opacity={refreshing ? 0.6 : 1}
              cursor="pointer"
              pressStyle={{ opacity: 0.85 }}
              onPress={refreshExternalPaymentStatus}
            >
              {refreshing ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text fontSize={15} fontWeight="800" color={colors.light}>
                  重新整理付款狀態
                </Text>
              )}
            </YStack>
            <YStack
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={() => setView("payable")}
            >
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                改用信用卡付款
              </Text>
            </YStack>
          </YStack>
        </YStack>
      </YStack>
    );
  }

  if (view === "success" || view === "paid") {
    const tx = status?.transaction;
    const amount = view === "success" ? (successInfo?.amount ?? 0) : (tx?.amount ?? status?.amount ?? 0);
    const label = view === "success" ? (successInfo?.label ?? "信用卡") : tx?.lastfour ? `信用卡 •••• ${tx.lastfour}` : (status?.label ?? "信用卡");
    const timeText =
      view === "success" ? (successInfo?.time ?? "") : tx?.paid_at ? new Date(tx.paid_at).toLocaleString("zh-TW", { hour12: false }) : "";
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <RadiusContentWrapper>
          <YStack ai="center" gap="$3" pt="$6">
            <View w={72} h={72} br={36} bg="#eafaf0" ai="center" jc="center">
              <RiCheckLine size={36} color={colors.green} />
            </View>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              {view === "success" ? "付款成功" : "這筆訂單已完成付款"}
            </Text>
            <Text fontSize={12} color={colors.sub} ta="center">
              {view === "success" ? "已通知瓦斯行，訂單狀態同步更新" : timeText ? `${timeText} 已完成付款` : "已完成付款"}
            </Text>
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" width="100%" gap="$1.5">
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款金額
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                  NT$ {amount.toLocaleString()}
                </Text>
              </XStack>
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款方式
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text}>
                  {label}
                </Text>
              </XStack>
              {view === "success" && successInfo?.time ? (
                <XStack jc="space-between">
                  <Text fontSize={13} color={colors.sub}>
                    時間
                  </Text>
                  <Text fontSize={13} fontWeight="700" color={colors.text}>
                    {successInfo.time}
                  </Text>
                </XStack>
              ) : null}
            </YStack>
            {justPaid ? <OaRedirectCountdown url={status?.oa_chat_url} supplierName={supplierName} /> : null}
            <YStack
              width="100%"
              br={12}
              py="$3"
              ai="center"
              bw={1}
              bc={colors.gray100}
              bg={colors.light}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={back}
            >
              <Text fontSize={14} fontWeight="700" color={colors.text}>
                查看訂單詳細
              </Text>
            </YStack>
            {isOaFriend === false && status?.add_friend_url ? <AddFriendCard supplierName={supplierName} url={status.add_friend_url} /> : null}
          </YStack>
        </RadiusContentWrapper>
      </YStack>
    );
  }

  if (view === "not_payable") {
    const st = status?.order_status;
    const hint = notPayableHint(status);
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text}>
            這筆訂單尚未開放線上付款
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            訂單狀態：{orderStatusLabel(st)}。{hint}
          </Text>
        </YStack>
      </YStack>
    );
  }

  // view === "payable"
  const myCards = cards.filter((c) => c.is_owned_by_caller);
  const sharedCards = cards.filter((c) => !c.is_owned_by_caller);
  // 不要求在 LINE App 內：外部 Safari 開 LIFF 連結登入後的 LINE 模式頁也要顯示按鈕；
  // 按下後 openPaymentUrl 依 liff.isInClient() 分流開啟方式。
  // devOverride：開發環境不受裝置限制（略過 os 檢查），方便桌面 dev 測試；
  // 與 Kiosk 結帳頁 CheckoutGuide 的 Apple Pay 判斷一致。
  const showApplePayInLine = shouldOfferApplePayInLine({
    os: liff.getOS() ?? null,
    paymentMethod: status?.payment_method ?? null,
    payable: status?.payable ?? false,
    devOverride: process.env.NODE_ENV === "development",
  });
  // 配送員現場把這筆改成一卡通 MONEY（掃碼付）：LINE 推播進來也要看得到一卡通，排在信用卡前面
  const showIpassInLine = status?.payment_method === "ipass_twqr" && (status?.payable ?? false);
  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      {headerNode}
      <RadiusContentWrapper space="$4">
        <YStack>
          <SectionLabel title="訂單摘要" />
          <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" gap="$1">
            {items.map((it, idx) => {
              const title = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : (it.commodity_name ?? "商品");
              const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;
              return (
                <Text key={idx} fontSize={13} color={colors.sub}>
                  {title} × {qty ?? 1}
                </Text>
              );
            })}
            <XStack jc="space-between" ai="baseline" mt="$2" pt="$2" btw={1} btc={colors.borderLight}>
              <Text fontSize={12} color={colors.sub}>
                應付金額
              </Text>
              <Text fontSize={22} fontWeight="800" color={colors.primary} style={{ fontVariantNumeric: "tabular-nums" }}>
                NT$ {(status?.amount ?? 0).toLocaleString()}
              </Text>
            </XStack>
          </YStack>
        </YStack>

        {showIpassInLine ? (
          <YStack>
            <SectionLabel title="一卡通 MONEY" />
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" gap="$3">
              <XStack ai="center" gap="$2.5">
                <View w={42} h={28} br={6} bw={1} bc="#e6e9f0" bg={colors.light} ai="center" jc="center" overflow="hidden">
                  <img src={ipassMoneyLogo} alt="iPASS MONEY" style={{ width: 34, height: 20, objectFit: "contain" }} />
                </View>
                <Text flex={1} fontSize={12.5} color={colors.sub} lineHeight={18}>
                  配送員已為這筆改用一卡通付款。會開啟付款頁，手機直接開 iPASS MONEY APP 付這一筆，付完回到這裡確認
                </Text>
              </XStack>
              <YStack
                br={12}
                py="$3"
                ai="center"
                bg={issuingToken ? colors.border : "#1E5EC8"}
                cursor={issuingToken ? "default" : "pointer"}
                pressStyle={{ opacity: issuingToken ? 1 : 0.85 }}
                onPress={openIpassTwqrExternal}
                role="button"
                aria-disabled={issuingToken}
                testID="ipass-pay-button"
              >
                <Text fontSize={15} fontWeight="800" color={issuingToken ? colors.hint : colors.light}>
                  {issuingToken ? "開啟中…" : `用 iPASS MONEY 付款 NT$ ${(status?.amount ?? 0).toLocaleString()}`}
                </Text>
              </YStack>
            </YStack>
            <Text fontSize={12} color={colors.hint} ta="center" mt="$3">
              — 或用信用卡 —
            </Text>
          </YStack>
        ) : null}

        <YStack>
          <SectionLabel title="選擇付款卡" />
          {cards.length === 0 ? (
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$5" ai="center" gap="$1">
              <RiBankCardLine size={26} color={colors.hint} />
              <Text fontSize={13.5} fontWeight="700" color={colors.text}>
                還沒有可用的信用卡
              </Text>
              <Text fontSize={11.5} color={colors.sub}>
                先綁定一張卡，之後付款一鍵完成
              </Text>
            </YStack>
          ) : (
            <YStack gap={10}>
              {myCards.map((c) => (
                <PaymentOptionRow
                  key={c.payment_method_id}
                  title={cardLabel(c)}
                  sub={c.card?.issuer || c.nickname || "信用卡"}
                  leading={<CardBrand bincode={c.card?.bincode} />}
                  selected={selectedId === c.payment_method_id}
                  onPress={() => setSelectedId(c.payment_method_id)}
                />
              ))}
              {sharedCards.length > 0 ? (
                <YStack mt="$1" gap={10}>
                  <SectionLabel title="帳戶共用卡" />
                  {sharedCards.map((c) => (
                    <PaymentOptionRow
                      key={c.payment_method_id}
                      title={cardLabel(c)}
                      sub={c.added_by_name ? `${c.added_by_name} 提供` : "帳戶共用卡"}
                      leading={<CardBrand bincode={c.card?.bincode} />}
                      selected={selectedId === c.payment_method_id}
                      onPress={() => setSelectedId(c.payment_method_id)}
                    />
                  ))}
                </YStack>
              ) : null}
              <AddCardRow onPress={goBindCard} />
            </YStack>
          )}
          {/* TapPay 金流安全揭露（審核要求：付款頁需說明金流商與加密／PCI 認證） */}
          <YStack mt="$2.5">
            <TapPaySecurityNote compact />
          </YStack>
        </YStack>

        <YStack gap="$2" pb="$6">
          {lockRemaining !== null ? <PaymentLockCountdownNote remainingMs={lockRemaining} /> : null}
          {cards.length === 0 ? (
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              cursor="pointer"
              pressStyle={{ opacity: 0.85 }}
              onPress={goBindCard}
            >
              <Text fontSize={15} fontWeight="800" color={colors.light}>
                綁定信用卡並付款
              </Text>
            </YStack>
          ) : (
            <YStack
              br={12}
              py="$3.5"
              ai="center"
              className="primary-bg-gradient"
              opacity={!selectedId || paying ? 0.6 : 1}
              cursor="pointer"
              pressStyle={{ opacity: 0.8 }}
              onPress={pay}
            >
              {paying ? (
                <Spinner size="small" color="#fff" />
              ) : (
                <Text fontSize={15} fontWeight="800" color={colors.light}>
                  確認付款 NT$ {(status?.amount ?? 0).toLocaleString()}
                </Text>
              )}
            </YStack>
          )}
          {showApplePayInLine ? (
            <>
              <Text fontSize={12} color={colors.hint} ta="center" my="$1">
                — 或 —
              </Text>
              <ApplePayButton state="ready" note="將立即付款 · 會開啟 Safari 完成" onPress={openApplePayExternal} />
            </>
          ) : null}
          <YStack ai="center" py="$2" cursor="pointer" pressStyle={{ opacity: 0.7 }} onPress={shareToOthers}>
            <Text fontSize={13} fontWeight="700" color={colors.primary}>
              {sharing ? "產生付款連結中…" : "請別人付款"}
            </Text>
            <Text fontSize={11} color={colors.hint} mt="$1">
              傳連結給家人或朋友，對方不用註冊就能付
            </Text>
          </YStack>
        </YStack>
      </RadiusContentWrapper>
    </YStack>
  );
};

export default PayOrder;
